import { benchSeatTopMm } from "./benchHeight";
import {
	BENCH_SLAT_GAP_MM,
	BENCH_SLAT_THICKNESS_MM,
	BENCH_SLAT_WIDTH_MM,
} from "./constants";
import { benchSupportFits } from "./benchSupport";
import {
	benchRunFootprint,
	footprintFromWallSpans,
	wallRunsAlongX,
	type BenchRun,
	type InteriorRoom,
} from "./types";
import type { BenchVolume } from "./benchCollision";
import { WALL_H_M } from "../geometry/buildingDimensions";

export interface BenchBackrest {
	boards: BenchVolume[];
	spacers: BenchVolume[];
	lengthMm: number;
	heightMm: number;
	lpm: number;
}

/** Planning spacing, not a verified fixing/board load rating. */
const BACKREST_SPACER_SPACING_MM = 600;

/** Wall-mounted slats on vertical 28×90 spacers, all in finished-floor mm. */
export function resolveBenchBackrest(
	run: BenchRun,
	room: InteriorRoom,
): BenchBackrest | null {
	if (!run.backrestEnabled) return null;
	const start = run.backrestStartMm ?? 0;
	const length = run.backrestLengthMm ?? run.lengthMm;
	const gap = run.backrestAboveSeatMm ?? 180;
	const count = run.backrestBoardCount ?? 2;
	const height = count * BENCH_SLAT_WIDTH_MM + (count - 1) * BENCH_SLAT_GAP_MM;
	const bottom = benchSeatTopMm(run, room.wallHeightMm) + gap;
	if (
		!benchSupportFits({ ...run, supportMode: "wall" }, room) ||
		![start, length, gap, count].every(Number.isFinite) ||
		start < 0 ||
		length < 300 ||
		start + length > run.lengthMm ||
		gap < 0 ||
		!Number.isInteger(count) ||
		count < 1 ||
		count > 4 ||
		bottom + height > (room.wallHeightMm ?? WALL_H_M * 1000)
	)
		return null;
	const fp = benchRunFootprint(run, room);
	const alongX = wallRunsAlongX(run.wall);
	const u0 = (alongX ? fp.minXMm : fp.minZMm) + start;
	const wallAtLow = run.wall === "back" || run.wall === "left";
	const v0 = alongX
		? wallAtLow
			? fp.minZMm
			: fp.maxZMm
		: wallAtLow
			? fp.minXMm
			: fp.maxXMm;
	const sign = wallAtLow ? 1 : -1;
	const t = BENCH_SLAT_THICKNESS_MM;
	const volume = (
		ua: number,
		ub: number,
		va: number,
		vb: number,
		lo: number,
		hi: number,
	): BenchVolume => ({
		...footprintFromWallSpans(
			run.wall,
			{ min: ua, max: ub },
			{ min: Math.min(va, vb), max: Math.max(va, vb) },
		),
		minYMm: lo,
		maxYMm: hi,
	});
	const boards = Array.from({ length: count }, (_, i) => {
		const y = bottom + i * (BENCH_SLAT_WIDTH_MM + BENCH_SLAT_GAP_MM);
		return volume(
			u0,
			u0 + length,
			v0 + sign * t,
			v0 + sign * 2 * t,
			y,
			y + BENCH_SLAT_WIDTH_MM,
		);
	});
	const spacerCount = Math.max(
		3,
		Math.ceil((length - BENCH_SLAT_WIDTH_MM) / BACKREST_SPACER_SPACING_MM) + 1,
	);
	const halfWidth = BENCH_SLAT_WIDTH_MM / 2;
	const spacers = Array.from({ length: spacerCount }, (_, i) => {
		const u =
			u0 + halfWidth + (i * (length - BENCH_SLAT_WIDTH_MM)) / (spacerCount - 1);
		return volume(
			u - halfWidth,
			u + halfWidth,
			v0,
			v0 + sign * t,
			bottom,
			bottom + height,
		);
	});
	return {
		boards,
		spacers,
		lengthMm: length,
		heightMm: height,
		lpm: (count * length + spacerCount * height) / 1000,
	};
}
